import type { CSSProperties } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { SledTeam, TeamRepoScore, DiscoverySummary, DiscoveryFeatureListView } from '@sdd-maturity/shared-types';
import { Button } from '../components/Button';
import { useApi } from '../hooks/useApi';
import { resolveScope, childLabel } from '../lib/scope';
import { buildScopePath, ROOT_NUMBER } from '../lib/hierarchy-route';
import { deriveStandings, deliveryAvg, fetchRootStandingsRollup, type StandingsRow } from '../lib/standings';
import { scoreColor } from '../lib/score';
import { modeMeta } from '../lib/scoreMode';
import { classifyRepos } from '../lib/utils';
import { buildMaturityGradient, countRepoLevels, countFeatureLevels } from '../lib/maturity-gradient';
import type { RollupData } from '../lib/rollup';

/**
 * Scope-adaptive entry (FR-2). Scope comes from the URL-resolved `node` (the root
 * "Cox Automotive" scope, a Portfolio/Stream/Train, or a single Team). At multi-team
 * scope it shows a Standings comparison — each child's Discovery & Delivery score
 * side by side, no blend. At single-team scope it collapses to a two-phase scorecard,
 * the only surface that shows both scores together.
 */
export function Overview({ node, teams }: { node: SledTeam; teams: SledTeam[] }) {
  const navigate = useNavigate();
  const scope = resolveScope(node, teams);

  // Drill a Standings row into the child's scope via the URL — `dest` decides where
  // you land: the child's own Overview (name click) or a phase view (score click).
  const drillInto = (child: SledTeam, dest: DrillDest): void => {
    navigate(buildScopePath(child, dest));
  };

  // ONE fetch for the whole multi-team scope: this scope's own rollup already embeds
  // a per-direct-child leaderboard (TASK-6 buildLeaderboard), so it feeds BOTH the
  // scorecard AND the Standings table below — no per-child fan-out (was O(children)
  // `getRollup` calls via fetchStandings, now a flat 2 regardless of scope size).
  // EXCEPTION: the synthetic root ("Cox Automotive") has no materialized rollup of
  // its own (rollup-sweep only computes real SLED tiers) — fall back to fetching
  // each top-level Portfolio's own rollup there (see fetchRootStandingsRollup).
  const { data: rollup, isLoading, error, refetch } = useApi<RollupData | null>(
    (client, token) => {
      if (scope.isSingleTeam || !scope.node) return Promise.resolve(null);
      if (scope.node.number === ROOT_NUMBER) return fetchRootStandingsRollup(scope.children, client, token);
      return client.getRollup(node.number, token).catch(() => null);
    },
    [node.number],
  );

  // Single-team scope: two-phase scorecard.
  if (scope.isSingleTeam) {
    return <TeamScorecard teamNumber={node.number} />;
  }

  const rows = deriveStandings(scope.children, rollup ?? null);

  return (
    <div data-testid="overview-standings">
      {/* Scope + lens are shown in the page header (breadcrumb → title → tabs). */}
      <ScopeScorecard node={node} rollup={rollup ?? null} />
      {isLoading ? (
        <div className="card" style={{ padding: 'var(--genai-space-lg)' }} data-testid="standings-loading">
          <p className="text-secondary">Loading standings…</p>
        </div>
      ) : error ? (
        <div className="card" style={{ padding: 'var(--genai-space-lg)', borderColor: 'var(--genai-sem-error)' }} data-testid="standings-error">
          <p className="text-lg font-semibold" style={{ color: 'var(--genai-sem-error)' }}>Failed to load standings</p>
          <p className="text-secondary mt-sm">{error}</p>
          <Button variant="secondary" className="mt-md" onClick={refetch}>Retry</Button>
        </div>
      ) : (
        <StandingsTable rows={rows} onDrill={drillInto} childLabels={childLabel(node)} />
      )}
    </div>
  );
}

type DrillDest = 'overview' | 'discovery' | 'delivery';

export function StandingsTable({ rows, onDrill, childLabels }: { rows: StandingsRow[]; onDrill: (node: SledTeam, dest: DrillDest) => void; childLabels: { singular: string; plural: string } }) {
  return (
    <div className="card card-flush" style={{ padding: 0, overflow: 'hidden' }} data-testid="standings-table-wrapper">
      <table className="repo-table" aria-label={`${childLabels.singular} standings by phase`}>
        <thead>
          <tr>
            <th>{childLabels.singular}</th>
            <th className="text-center">Discovery</th>
            <th className="text-center">Delivery</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={3} className="text-center text-secondary">{`No ${childLabels.plural.toLowerCase()} under this scope`}</td></tr>
          )}
          {rows.map((row) => (
            <tr key={row.node.number} data-testid={`standings-row-${row.node.number}`}>
              <td>
                <div className="stacked-cell">
                  {/* Name drills into the child's own Overview (a team → its scorecard;
                      a stream/train → its standings one level down). */}
                  <button
                    type="button"
                    onClick={() => onDrill(row.node, 'overview')}
                    data-testid={`standings-name-${row.node.number}`}
                    className="font-medium"
                    style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 'var(--genai-text-base)', color: 'var(--genai-sem-accent)', cursor: 'pointer', textAlign: 'left' }}
                  >
                    {row.node.name}
                  </button>
                  {row.teamCount > 1 && (
                    <span className="text-xs text-muted">{row.teamCount} teams</span>
                  )}
                </div>
              </td>
              <ScoreCell phase="discovery" score={row.discoveryScore} node={row.node} onDrill={onDrill} />
              <ScoreCell phase="delivery" score={row.deliveryScore} node={row.node} onDrill={onDrill} />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** A single phase score cell. Drills into the child's scope then opens that phase
 * (no blended number) — so a team's score lands on its phase view, and a
 * stream/train's score lands on that scope's phase rollup. */
function ScoreCell({ phase, score, node, onDrill }: {
  phase: 'discovery' | 'delivery';
  score: number | null;
  node: SledTeam;
  onDrill: (node: SledTeam, dest: DrillDest) => void;
}) {
  return (
    <td className="text-center">
      {score === null ? (
        <span className="text-muted">—</span>
      ) : (
        <button
          type="button"
          onClick={() => onDrill(node, phase)}
          data-testid={`standings-${phase}-link`}
          className="text-base font-bold"
          style={{ background: 'none', border: 'none', padding: 0, color: scoreColor(score), cursor: 'pointer' }}
        >
          {score}
        </button>
      )}
    </td>
  );
}

/**
 * The shared two-`PhaseCard` grid used by both the single-team scorecard and the
 * multi-team scope rollup. Callers own their own data fetch and pass the two phase
 * scores + level counts; only the wrapper `testId`/`className` differ.
 */
function TwoPhaseScorecard({ testId, className, node, discoveryScore, deliveryScore, discoveryLevels, deliveryLevels }: {
  testId: string;
  className: string;
  node: { tier: number; number: string };
  discoveryScore: number | null;
  deliveryScore: number | null;
  discoveryLevels?: Record<string, number>;
  deliveryLevels?: Record<string, number>;
}) {
  return (
    <div className={className} data-testid={testId}>
      {/* Scope/team name + lens are shown in the page header. */}
      <PhaseCard phase="discovery" score={discoveryScore} node={node} levelCounts={discoveryLevels} />
      <PhaseCard phase="delivery" score={deliveryScore} node={node} levelCounts={deliveryLevels} />
    </div>
  );
}

/** Single-team two-phase scorecard (FR-2.2) — the only surface showing both scores together. */
function TeamScorecard({ teamNumber }: { teamNumber: string }) {
  const { data: discovery } = useApi<DiscoverySummary | null>(
    (client, token) => client.getDiscoverySummary(teamNumber, token).catch(() => null),
    [teamNumber],
  );
  const { data: reposData } = useApi<{ repos: TeamRepoScore[] }>(
    (client, token) => client.getTeamRepos(teamNumber, token),
    [teamNumber],
  );
  // Per-item discovery levels aren't in the summary, so fetch the feature scores
  // to color the Discovery card's gradient (one extra request at team scope).
  const { data: features } = useApi<DiscoveryFeatureListView[] | null>(
    (client, token) => client.getDiscoveryScores(teamNumber, token).catch(() => null),
    [teamNumber],
  );
  // A team with no aggregate row at all (never scanned — real or synthetic) comes
  // back as scored: 0, partial: 0, softSkipped: 0, overallScore: 0, zeroScorable:
  // false (queryTeamDiscoverySummary has nothing to floor `zeroScorable` on). Gate
  // on the union of all three bucket counts, not just `scored` — overallScore is
  // computed across all three buckets server-side (score-everything mode), so a
  // team with only partial-sdd/not-sdd features (scored === 0) still has a real
  // average to show, not just the "nothing was ever measured" degrade (AC-1.4).
  const discoveryHasScores = discovery && (discovery.scored > 0 || discovery.partial > 0 || discovery.softSkipped > 0);
  const discoveryScore = discovery && !discovery.zeroScorable && discoveryHasScores ? discovery.overallScore ?? null : null;
  const deliveryScore = deliveryAvg(reposData?.repos ?? []);
  const discoveryLevels = features ? countFeatureLevels(features) : undefined;
  const deliveryLevels = reposData ? countRepoLevels(classifyRepos(reposData.repos).active) : undefined;

  return (
    <TwoPhaseScorecard
      testId="overview-scorecard"
      className="grid grid-cols-2 gap-md"
      node={{ tier: 4, number: teamNumber }}
      discoveryScore={discoveryScore}
      deliveryScore={deliveryScore}
      discoveryLevels={discoveryLevels}
      deliveryLevels={deliveryLevels}
    />
  );
}

/**
 * Multi-team two-phase rollup shown above the Standings table at every non-leaf scope
 * (root / Portfolio / Stream / Train). The scores are the scope's `getRollup` averages —
 * the same `discoveryAvg`/`deliveryAvg` shown on that scope's phase tab — so a scope's
 * number is identical across the app. Purely presentational — the ONE rollup fetch for
 * this scope lives in `Overview` (shared with the Standings table below, no duplicate
 * fetch); a null/failed rollup (or a scope with no scored teams) degrades to "—".
 */
function ScopeScorecard({ node, rollup }: { node: SledTeam; rollup: RollupData | null }) {
  const hasTeams = rollup !== null && rollup !== undefined && rollup.teamCount > 0;
  const discoveryScore = hasTeams ? rollup.discoveryAvg : null;
  const deliveryScore = hasTeams ? rollup.deliveryAvg : null;
  // Read each mode's already-deduped levelCounts directly — summing leaderboard
  // rows here would double-count a repo/feature whose owning team is mapped
  // under more than one row (the 771-vs-897 defect; see ModeRollup.levelCounts).
  const discoveryLevels = hasTeams ? rollup.discovery.levelCounts : undefined;
  const deliveryLevels = hasTeams ? rollup.delivery.levelCounts : undefined;

  return (
    <TwoPhaseScorecard
      testId="overview-scope-rollup"
      className="grid grid-cols-2 gap-md mb-md"
      node={node}
      discoveryScore={discoveryScore}
      deliveryScore={deliveryScore}
      discoveryLevels={discoveryLevels}
      deliveryLevels={deliveryLevels}
    />
  );
}

export function PhaseCard({ phase, score, node, levelCounts }: { phase: 'discovery' | 'delivery'; score: number | null; node: { tier: number; number: string }; levelCounts?: Record<string, number> }) {
  const label = modeMeta(phase).label;
  const blurb = modeMeta(phase).cardBlurb;
  // Subtle corner glow reflecting the scope's L1–L5 maturity mix (see .phase-card).
  const gradient = levelCounts ? buildMaturityGradient(levelCounts) : null;
  return (
    <div
      className="card phase-card"
      data-testid={`phase-card-${phase}`}
      style={gradient ? ({ '--maturity-gradient': gradient } as CSSProperties) : undefined}
    >
      <div className="flex items-center justify-between mb-sm">
        <h3 className="text-lg font-semibold" style={{ margin: 0 }}>{label}</h3>
      </div>
      <p className="text-base text-muted" style={{ marginTop: 0 }}>{blurb}</p>
      <div className="text-4xl font-bold leading-tight" style={{ color: score === null ? 'var(--genai-sem-text-muted)' : scoreColor(score) }}>
        {score === null ? '—' : score}
      </div>
      <Link
        to={buildScopePath(node, phase)}
        data-testid={`open-${phase}`}
        className="ids-btn ids-btn--secondary ids-btn--m"
        style={{ textDecoration: 'none', marginTop: 'var(--genai-space-md)' }}
      >
        Open {label} →
      </Link>
    </div>
  );
}
